import React, { useState, useRef, useEffect } from 'react';
import { isDayAllowed, getTimeBounds } from '../../../utils/dateConstraints';

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];
const DAY_HEADERS = ['Su','Mo','Tu','We','Th','Fr','Sa'];

const splitValue = (value) => {
  if (!value) return { date: '', time: '' };
  const [date, time] = value.split('T');
  return { date: date || '', time: (time || '').slice(0, 5) };
};

const parseISO = (iso) => {
  if (!iso) return null;
  const d = new Date(iso + 'T00:00:00');
  return isNaN(d.getTime()) ? null : d;
};

const toISODate = (year, month, day) =>
  `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

const formatTime12h = (hhmm) => {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${period}`;
};

/** Same calendar UI as DatePicker, plus a native time-of-day control in the
 * popover — combined into one `YYYY-MM-DDTHH:mm` value. The time input keeps
 * its own class (not `.form-input`) specifically so it doesn't inherit the
 * `appearance: none` reset that makes native datetime-local time segments
 * unclickable in most browsers. */
const DateTimePicker = ({
  value = '',
  onChange,
  placeholder = 'Select date & time',
  required,
  id,
  name,
  disabled,
  dateConstraint, // 'past' | 'future' | undefined — today and earlier/later only
}) => {
  const { date: datePart, time: timePart } = splitValue(value);
  const today = new Date();
  const parsed = parseISO(datePart);

  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState((parsed || today).getFullYear());
  const [viewMonth, setViewMonth] = useState((parsed || today).getMonth());
  const [yearInput, setYearInput] = useState(false);
  const [yearDraft, setYearDraft] = useState(String((parsed || today).getFullYear()));

  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === 'Escape') { setOpen(false); return; }
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', handler);
    };
  }, [open]);

  useEffect(() => {
    if (parsed) {
      setViewYear(parsed.getFullYear());
      setViewMonth(parsed.getMonth());
    }
  }, [datePart]); // eslint-disable-line react-hooks/exhaustive-deps

  const emit = (nextDate, nextTime) => {
    const d = nextDate ?? datePart;
    const t = nextTime ?? (timePart || '00:00');
    onChange?.({ target: { value: d ? `${d}T${t}` : '' } });
  };

  const displayValue = parsed
    ? `${parsed.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}${timePart ? ` at ${formatTime12h(timePart)}` : ''}`
    : '';

  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const firstDay = new Date(viewYear, viewMonth, 1).getDay();

  const isSelected = (day) =>
    parsed &&
    parsed.getFullYear() === viewYear &&
    parsed.getMonth() === viewMonth &&
    parsed.getDate() === day;

  const isToday = (day) =>
    today.getFullYear() === viewYear &&
    today.getMonth() === viewMonth &&
    today.getDate() === day;

  const isDayDisabled = (day) => !isDayAllowed(dateConstraint, toISODate(viewYear, viewMonth, day));

  const pickDay = (day) => {
    if (isDayDisabled(day)) return;
    emit(toISODate(viewYear, viewMonth, day), timePart || '09:00');
  };
  const pickToday = () => emit(toISODate(today.getFullYear(), today.getMonth(), today.getDate()), timePart || '09:00');
  const handleTimeChange = (e) => emit(datePart || toISODate(today.getFullYear(), today.getMonth(), today.getDate()), e.target.value);
  const timeBounds = getTimeBounds(dateConstraint, datePart);

  const clearAll = (e) => {
    e.stopPropagation();
    onChange?.({ target: { value: '' } });
  };

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };
  const commitYearInput = () => {
    const y = parseInt(yearDraft, 10);
    if (!isNaN(y) && y >= 1900 && y <= 2100) setViewYear(y);
    setYearInput(false);
  };

  return (
    <div className="dp-wrap" ref={wrapRef}>
      <div
        className={`dp-trigger${open ? ' is-open' : ''}${disabled ? ' is-disabled' : ''}`}
        onClick={() => !disabled && setOpen(o => !o)}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={e => !disabled && e.key === 'Enter' && setOpen(o => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <span className="dp-trigger-icon">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="9" />
            <polyline points="12 7 12 12 15 14" />
          </svg>
        </span>
        <span className={`dp-trigger-value${!displayValue ? ' is-placeholder' : ''}`}>
          {displayValue || placeholder}
        </span>
        {value && !disabled && (
          <button className="dp-clear-btn" type="button" onClick={clearAll} aria-label="Clear date & time" tabIndex={-1}>×</button>
        )}
        <span className={`dp-caret${open ? ' is-open' : ''}`}>
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </div>

      {open && (
        <div className="dp-calendar" role="dialog" aria-label="Date and time picker">
          <div className="dp-nav">
            <button type="button" className="dp-nav-btn" onClick={prevMonth} aria-label="Previous month">‹</button>
            <div className="dp-nav-center">
              <span className="dp-nav-month">{MONTHS[viewMonth]}</span>
              {yearInput ? (
                <input
                  className="dp-year-input"
                  type="number"
                  value={yearDraft}
                  onChange={e => setYearDraft(e.target.value)}
                  onBlur={commitYearInput}
                  onKeyDown={e => { if (e.key === 'Enter') commitYearInput(); if (e.key === 'Escape') setYearInput(false); }}
                  autoFocus
                />
              ) : (
                <button type="button" className="dp-nav-year" onClick={() => { setYearDraft(String(viewYear)); setYearInput(true); }} title="Click to change year">
                  {viewYear}
                </button>
              )}
            </div>
            <button type="button" className="dp-nav-btn" onClick={nextMonth} aria-label="Next month">›</button>
          </div>

          <div className="dp-grid">
            {DAY_HEADERS.map(d => <div key={d} className="dp-day-header">{d}</div>)}
            {Array.from({ length: firstDay }).map((_, i) => <div key={`e${i}`} />)}
            {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => (
              <button
                key={day}
                type="button"
                disabled={isDayDisabled(day)}
                className={[
                  'dp-day',
                  isSelected(day) ? 'is-selected' : '',
                  isToday(day) && !isSelected(day) ? 'is-today' : '',
                  isDayDisabled(day) ? 'is-disabled' : '',
                ].filter(Boolean).join(' ')}
                onClick={() => pickDay(day)}
                aria-label={`${MONTHS[viewMonth]} ${day}, ${viewYear}`}
                aria-pressed={!!isSelected(day)}
              >
                {day}
              </button>
            ))}
          </div>

          <div className="dp-time-row">
            <label className="dp-time-label" htmlFor={`${id || name}-time`}>Time</label>
            <input
              id={`${id || name}-time`}
              type="time"
              className="dp-time-input"
              value={timePart}
              onChange={handleTimeChange}
              min={timeBounds.min}
              max={timeBounds.max}
            />
          </div>

          <div className="dp-footer">
            <button type="button" className="dp-today-btn" onClick={pickToday}>Today, now</button>
            <button type="button" className="dp-done-btn" onClick={() => setOpen(false)}>Done</button>
          </div>
        </div>
      )}

      <input type="hidden" id={id} name={name} value={value || ''} required={required} />
    </div>
  );
};

export default DateTimePicker;
